import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Permissions } from '@shared/interfaces/Permissions';
import { AuthUtils } from '@core/auth/auth.utils';

@Injectable({
    providedIn: 'root',
})
export class PermitService {
  
  /**
   * Indicates if the user is an admin.
   */
  isAdmin: boolean;

  /**
   * List of permissions for the user.
   */
  listaPermisos: string[] = [];

  /**
   * Permissions object with default values.
   */
  permissions: Permissions = {
    accesoCrearAfiliacion: false,
    accesoControlAfiliaciones: false,
    accesoAnsOperacion: false,
    accesoInformesAfiliacion: false,
    accesoConvocatoriasMasivas: false,
    accesoBancoTalento: false,
    accesoIgresoIncapacidades: false,
    accesoGestionIncapacidades: false,
    accesoHistorialIncapacidad: false,
    accesoReportesGenerales: false,
    crearProcesoContratacion: false,
    controlProcesosContratacion: false,
    accesoHistorialAfiliaciones: false,
    accesoGestionAfiliacion: false,
    accesoAnulaciones: false,
    accesoInscribirEntidad: false,
    accesoEntidadesInscritas: false,
    accesoInscribirEmpresaCliente: false,
    accesoEmpresasClientesInscritas: false,
    accesoHistorialInscripcionEmpresasCliente: false,
    editarMetadatosControlProcesosContratacion: false,
    eliminarDocumentosControlProcesosContratacion: false,
    reemplazarDocumentosControlProcesosContratacion: false,
    accesoRadicacionExterna: false,
    verCorreosFirma: false,
    accesoEvidenciasCorreo: false,
    solicitarCopiaArchivada: false,
  }
  
  /**
   * BehaviorSubject to manage permissions state.
   */
  private readonly permisos = new BehaviorSubject<Permissions>(this.permissions);

  constructor() {
    this.isAdmin = this.obtenerPermisos();
    if (this.isAdmin) {
      this.todosAccesos();
    } else {
      this.obtenerAccesos();
    }
  }

  /**
   * Refreshes the permissions by re-reading the token from localStorage.
   * Should be called after login or when the token changes.
   */
  refreshPermissions(): void {
    // Reset permissions to false
    Object.keys(this.permissions).forEach(key => {
        this.permissions[key] = false;
    });
    this.setPermisos(this.permissions);

    this.isAdmin = this.obtenerPermisos();
    if (this.isAdmin) {
      this.todosAccesos();
    } else {
      this.obtenerAccesos();
    }
  }

  /**
   * Retrieves permissions from the token stored in localStorage.
   * @returns {boolean} True if the user is an admin, otherwise false.
   */
  obtenerPermisos(): boolean {
    const token = localStorage.getItem('permissionsToken');
    if (token) {
      return AuthUtils._getTokenUser(token);
    }
    return false;
  }

  /**
   * Obtains access permissions from the token and verifies them.
   */
  obtenerAccesos(): void {
    const token = localStorage.getItem('permissionsToken');
    if (token) {
      this.listaPermisos = AuthUtils._getTokenPermissionRol(token);
      if (this.listaPermisos) {
        this.verificaPermisos();
      }
    } else {
      console.log('No token found in localStorage');
    }
  }

  /**
   * Verifies and sets the permissions based on the list of permissions.
   */
  verificaPermisos(): void {
    this.listaPermisos.forEach((permiso) => {
      const nombrePropiedad = this.snakeCaseToCamelCase(permiso);
      if (this.permissions.hasOwnProperty(nombrePropiedad)) {
        this.permissions[nombrePropiedad] = true;
      }
    });
    this.setPermisos(this.permissions);
  }

  /**
   * Converts a snake_case string to camelCase.
   * @param {string} str - The snake_case string.
   * @returns {string} The camelCase string.
   */
  snakeCaseToCamelCase(str: string): string {
    return str.replace(/_([a-z])/g, (match, letra) => letra.toUpperCase());
  }

  /**
   * Grants all access permissions.
   */
  todosAccesos(): void {
    Object.keys(this.permissions).forEach((permission) => this.permissions[permission] = true);
    this.setPermisos(this.permissions);
  }

  /**
   * Returns an observable of the permissions.
   * @returns {Observable<Permissions>} The permissions observable.
   */
  getPermisos():Observable<Permissions> {
    return this.permisos.asObservable();
  }

  /**
   * Sets the permissions and updates the BehaviorSubject.
   * @param {Permissions} permisos - The permissions to set.
   */
  setPermisos(permisos: Permissions): void {
    this.permisos.next(permisos);
  }

  /**
   * Getter that checks if the current user has admin privileges.
   * 
   * @returns {boolean} True if the user is an admin, otherwise false.
   */
  get getIsAdmin(): boolean {
    return this.isAdmin;
  }
}
